// What Call Sheet needs to run a puzzle.
//
// As with Running Order, whatever the server hands over is already resolved:
// names and image paths are read from the refreshed tables, never frozen into a
// puzzle record (CLAUDE.md: Cached TMDB data may be kept for at most six months).
import { tmdbImage } from '../../lib/tmdb.ts';

export interface Actor {
  id: number;             // TMDB person id
  name: string;
  photo: string | null;   // a TMDB profile path; null falls back to initials
}

export interface CastMember {
  name: string;
  role: string | null;
  /** Where this row goes, 1 upwards, decided by the server
   *  (`shared/result-cast.ts`). The screen sorts on this and on nothing else. */
  rank: number;
}

/**
 * The cast the result screen shows, **in the order the rank says and not in the
 * order the array happens to arrive in.**
 *
 * It used to return three groups concatenated — the revealed actors, then any
 * revealed actor the list had missed, then everybody else — each in whatever
 * order it was handed. That put the four first, which was the point, but every
 * other part of the order was the caller's doing: the fixture was written in
 * billing order, so it looked like a rule, and a differently ordered list would
 * have changed the screen silently.
 *
 * **The rule itself is not here, deliberately.** Which eight, and in what order,
 * is decided in `shared/result-cast.ts` against the database, because two of its
 * signals are server-side facts: the owner's hand-set lead, and recognisability,
 * which is a percentile over every photographed person. Neither belongs in a
 * browser, and a rule written in two places is one that will differ one day. What
 * is enforced here is that the screen is a function of the rows rather than of
 * their order, which is the part a client can be sure of.
 */
/**
 * **At most two parts per actor, then "..."** (owner, 12 October). A
 * credit lists every part an actor played, separated by slashes, biggest
 * first; on an anthology that ran to thirteen for Sarah Paulson on #3 *American
 * Horror Story*, six lines of the result screen for one actor.
 */
export function roleLabel(role: string): string {
  const parts = role.split(' / ').map((p) => p.trim()).filter(Boolean);
  if (parts.length <= 2) return parts.join(' / ');
  return `${parts.slice(0, 2).join(' / ')} ...`;
}

export function castForResult(cast: CastMember[], actors: { name: string }[]): CastMember[] {
  const ordered = [...cast].sort((a, b) => a.rank - b.rank);
  const revealed = new Set(actors.map((a) => a.name));
  const named = new Set(cast.map((m) => m.name));

  // **A safety net, and it should never fire.** The server sends the four the
  // puzzle revealed among the eight, each with the character from their own
  // credit. If one is ever missing, it is shown anyway and at the front rather
  // than dropped: a face the player was just shown is the one row the list must
  // not be without, even at the cost of having no part against it.
  const missing = actors.filter((a) => !named.has(a.name)).map((a) => ({ name: a.name, role: null }));
  if (!missing.length) return ordered;

  const lastRevealed = ordered.reduce((n, m, i) => revealed.has(m.name) ? i : n, -1);
  const out = [...ordered];
  out.splice(lastRevealed + 1, 0, ...missing.map((m, i) => ({ ...m, rank: lastRevealed + 1 + i })));
  return out;
}

export interface CallSheetAnswer {
  id: number;
  kind: 'movie' | 'tv';
  title: string;          // the display name if one is set, otherwise TMDB's
  year: number | null;
  endYear: number | null; // TV only: the year range's second half
  poster: string | null;
  cast: CastMember[];     // the billed cast, for the result screen
  /** Director for a film, creator for a series. Null where TMDB has neither
   *  — a miniseries often has no `created_by` at all, Band of Brothers
   *  among them — and the result screen then shows no line rather than an
   *  empty one. */
  directors: string[] | null;
}

/**
 * **A puzzle as the server lets a player see it** (cs_play), worked out afresh
 * from every guess so far. The actors are only those revealed -- two to start,
 * one more after each wrong guess -- and the answer is null until the game is
 * over, so an unrevealed face and the answer are never on this device early.
 */
export interface CallSheetPlay {
  number: number;                 // a pool puzzle is numbered, never dated
  /** A queued puzzle reached with the test code. */
  test: boolean;
  slots: number;
  /** One per guess: was it right. */
  results: boolean[];
  finished: boolean;
  won: boolean;
  actors: Actor[];                // revealed so far, in reveal order
  answer: CallSheetAnswer | null;
  /** How wide the puzzle's longest name needs its box, in em — all four names,
   *  the unrevealed ones included, so the faces are spaced once and never move
   *  (shared/name-width.ts). A width, never a name. */
  nameEm?: number | null;
}

/** Until a puzzle's own figure is known: the widest name in the pool, rounded
 *  up, so a name is never cut while waiting for it. */
export const NAME_EM_FALLBACK = 9.5;

export const MAX_GUESSES = 3;
// Two showing at the start, one more after each wrong guess, to a maximum of
// four (CLAUDE.md: Core rules).
export const OPENING_ACTORS = 2;
export const MAX_ACTORS = 4;

export function profileUrl(path: string | null): string | null {
  return tmdbImage(path, 'w342');
}

export function posterUrl(path: string | null): string | null {
  return tmdbImage(path, 'w342');
}

// How many faces are on screen after a given number of wrong guesses. Never
// fewer than the opening pair, never more than four.
export function revealedCount(wrongGuesses: number): number {
  return Math.min(MAX_ACTORS, OPENING_ACTORS + wrongGuesses);
}

// A year, or a year range for a series that has ended.
/**
 * "Directed by" for a film, "Created by" for a series, and nothing at all where
 * there is no name — not an empty line, because the gap would be as visible
 * as the text.
 */
export function directorLabel(answer: CallSheetAnswer): string {
  const names = answer.directors ?? [];
  if (!names.length) return '';
  return `${answer.kind === 'tv' ? 'Created by' : 'Directed by'} ${names.join(', ')}`;
}

export function yearLabel(answer: CallSheetAnswer): string {
  if (answer.year === null) return '';
  if (answer.kind === 'tv' && answer.endYear && answer.endYear !== answer.year) {
    return `${answer.year}–${answer.endYear}`;
  }
  return String(answer.year);
}
